/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { INfseEmitente, INfseEmpresaBusca } from '../../../../../types/nfseEmissao';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { EmpresaBusca } from './EmpresaBusca';

interface CertificadoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  emitentes: INfseEmitente[];
  // Emitente fixo (renovação de certificado de quem já emite).
  emitenteId?: number | null;
  // Empresa fixa que ainda não emite (escolhida na tela de emissão).
  empresaInicial?: INfseEmpresaBusca | null;
  onEnviado?: (emitenteId: number) => void;
}

// Envio do certificado A1 (.pfx + senha). É feito uma vez por empresa e de novo quando
// o certificado vence. O backend confere a senha e o CNPJ e guarda o arquivo cifrado.
// É esse envio que habilita a empresa a emitir.
export function CertificadoUploadModal({
  isOpen,
  onClose,
  emitentes,
  emitenteId,
  empresaInicial,
  onEnviado,
}: CertificadoUploadModalProps) {
  const queryClient = useQueryClient();
  const { enviarCertificado } = useNfseEmissao();
  const [empresa, setEmpresa] = useState<INfseEmpresaBusca | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');

  useEffect(() => {
    if (isOpen) {
      const fixo = emitentes.find(e => e.id === emitenteId);
      setEmpresa(
        empresaInicial ??
          (fixo
            ? {
                empresa_id: fixo.empresa_id,
                external_id: '',
                razao_social: fixo.razao_social,
                cnpj: fixo.cnpj,
                municipio: fixo.municipio,
                uf: fixo.uf,
                emitente_id: fixo.id,
              }
            : null),
      );
      setArquivo(null);
      setSenha('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, emitenteId, empresaInicial]);

  const mutation = useMutation({
    mutationFn: () =>
      enviarCertificado({
        empresa_id: (empresa as INfseEmpresaBusca).empresa_id,
        arquivo: arquivo as File,
        senha,
      }),
    onSuccess: cert => {
      toast.success(
        `Certificado enviado. Válido até ${new Date(cert.valido_ate).toLocaleDateString('pt-BR')}.`,
      );
      queryClient.invalidateQueries({ queryKey: ['nfse-certificados'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-emitentes'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-painel'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-ultima-nota'] });
      if (cert.emitente_id) onEnviado?.(cert.emitente_id);
      onClose();
    },
    onError: err =>
      toast.error(extractApiErrorMessage(err, 'Não foi possível enviar o certificado.')),
  });

  const podeEnviar = !!empresa && !!arquivo && !!senha && !mutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Enviar certificado digital</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" gap={4}>
          <Text fontSize="13px" color="gray.600">
            O certificado A1 da empresa assina as notas e autentica o envio à Sefin. Ele fica
            cifrado no servidor, e a senha nunca aparece na tela.
          </Text>
          <FormControl isRequired>
            <FormLabel fontWeight="bold" mb={1}>
              Empresa
            </FormLabel>
            <EmpresaBusca
              empresa={empresa}
              emitentes={emitentes}
              onSelecionar={setEmpresa}
              isDisabled={!!emitenteId || !!empresaInicial}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontWeight="bold" mb={1}>
              Arquivo do certificado (.pfx ou .p12)
            </FormLabel>
            <Input
              type="file"
              accept=".pfx,.p12"
              p={1}
              onChange={e => setArquivo(e.target.files?.[0] ?? null)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontWeight="bold" mb={1}>
              Senha do certificado
            </FormLabel>
            <Input
              type="password"
              autoComplete="new-password"
              value={senha}
              onChange={e => setSenha(e.target.value)}
            />
          </FormControl>
          <UnorderedList
            fontSize="12px"
            color="gray.600"
            bg="gray.50"
            borderRadius="md"
            p={3}
            pl={7}
          >
            <ListItem>A senha é conferida na hora.</ListItem>
            <ListItem>O CNPJ do certificado precisa ser o da empresa.</ListItem>
            <ListItem>O certificado anterior da empresa é desativado.</ListItem>
            <ListItem>Aceito o certificado, a empresa passa a poder emitir.</ListItem>
          </UnorderedList>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => mutation.mutate()}
            isDisabled={!podeEnviar}
            isLoading={mutation.isPending}
          >
            Enviar certificado
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
